import type { CallsheetAddedShot, CallsheetMissedShot } from './callsheetEvents';
import type { DeliverableType, DeliverableStatus } from './deliverables';
// Row types + API DTOs shared between worker and client.

export interface ProjectActivityEntry {
  id: number;
  kind: string;
  summary: string;
  created_at: number;
  callsheet_url: string | null;
}

export interface ProjectWrapNotes {
  callsheet_id: string;
  callsheet_title: string;
  callsheet_url: string;
  production: string;
  footage: string;
  issues: string;
  crew_added_shots: CallsheetAddedShot[];
  missed_shots: CallsheetMissedShot[];
  created_at: number;
  updated_at: number;
}

// ---------- Day journal ----------

export const JOURNAL_BODY_MAX = 500;

export interface JournalEntry {
  id: string;
  entry_date: string;
  body: string;
  created_at: number;
  updated_at: number;
  deleted_at: number | null;
}

// ---------- Clients ----------

export const CLIENT_STATUSES = ['lead', 'active', 'past', 'archived'] as const;
export type ClientStatus = (typeof CLIENT_STATUSES)[number];

export interface ClientRow {
  do_not_contact?: number;
  last_outreach_date?: string | null;
  reconnect_last_created_at?: number | null;
  id: string;
  name: string;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  website: string | null;
  address: string | null;
  secondary_contact_name: string | null;
  secondary_contact_email: string | null;
  dashboard_slug: string | null;
  testimonial_prompt?: string | null;
  payment_terms_days: number | null;
  status: ClientStatus;
  notes: string | null;
  notion_id: string | null;
  /** Phase 2.5 notification preference centre — 1/0, default 1 (opted in).
      Gates the corresponding automated client sends; operator-initiated
      one-off sends are never gated by these (see lib/prefsLink.ts's
      callers in the runners). */
  notify_invoices: number;
  notify_nudges: number;
  notify_milestones: number;
  /** Unguessable public-prefs-page token (migration 0029), generated on
      demand — see lib/prefsLink.ts's ensurePrefsToken. */
  prefs_token: string | null;
  created_at: number;
  updated_at: number;
  deleted_at: number | null;
}

export interface ClientWithCounts extends ClientRow {
  projects_total: number;
  projects_active: number;
}

export interface ClientUpsert {
  name: string;
  contact_name?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  website?: string | null;
  address?: string | null;
  secondary_contact_name?: string | null;
  secondary_contact_email?: string | null;
  dashboard_slug?: string | null;
  payment_terms_days?: number | null;
  status?: ClientStatus;
  notes?: string | null;
}

/** A person at a client company. Default contacts are mirrored into the
    legacy clients.contact_* columns for backwards-compatible readers. */
export interface ClientContactRow {
  id: string;
  client_id: string;
  name: string;
  email: string | null;
  phone: string | null;
  role_label: string | null;
  is_default: number;
  created_at: number;
  updated_at: number;
  deleted_at: number | null;
}

export interface ClientContactUpsert {
  name: string;
  email?: string | null;
  phone?: string | null;
  role_label?: string | null;
  is_default?: boolean;
}

// ---------- Leads ----------

export const LEAD_STATUSES = [
  'enquiry',
  'discovery_call',
  'proposal_creation',
  'proposal_sent',
  'negotiation',
  'locked_in',
  'ghosted',
  'won',
  'lost',
  'postponed',
  'void',
] as const;
export type LeadStatus = (typeof LEAD_STATUSES)[number];

/** Statuses that count as an open pipeline deal. */
export const LEAD_OPEN_STATUSES: readonly LeadStatus[] = [
  'enquiry',
  'discovery_call',
  'proposal_creation',
  'proposal_sent',
  'negotiation',
] as const;

export const LEAD_STATUS_LABELS: Record<LeadStatus, string> = {
  enquiry: 'Enquiry',
  discovery_call: 'Discovery call',
  proposal_creation: 'Proposal creation',
  proposal_sent: 'Proposal sent',
  negotiation: 'Negotiation',
  locked_in: 'Locked in',
  ghosted: 'Ghosted',
  won: 'Won',
  lost: 'Lost',
  postponed: 'Postponed',
  void: 'Void',
};

export const LEAD_SOURCES = [
  'facebook',
  'instagram',
  'linkedin',
  'repeat_client',
  'referral',
  'website',
  'other',
] as const;
export type LeadSource = (typeof LEAD_SOURCES)[number];

export const LEAD_SOURCE_LABELS: Record<LeadSource, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  repeat_client: 'Repeat client',
  referral: 'Referral',
  website: 'Website',
  other: 'Other',
};

/** Confirmed work that isn't a project yet. Excluded from open-pipeline
 * value, counted as a win in the conversion rate; `resurface_date` holds
 * its start date (the "Convert to project" nudge fires on/after it). */
export const LEAD_LOCKED_IN: LeadStatus = 'locked_in';

export const LEAD_REQUEST_TYPES = ['quote', 'proposal'] as const;
export type LeadRequestType = (typeof LEAD_REQUEST_TYPES)[number];

export const LEAD_REQUEST_TYPE_LABELS: Record<LeadRequestType, string> = {
  quote: 'Quote',
  proposal: 'Proposal',
};

/** `outbound` is reserved for the Marketing tab (handoff #6); the leads UI
 * only offers Paid / Organic. */
export const LEAD_ACQUISITIONS = ['paid', 'organic', 'outbound'] as const;
export type LeadAcquisition = (typeof LEAD_ACQUISITIONS)[number];

export const LEAD_ACQUISITION_LABELS: Record<LeadAcquisition, string> = {
  paid: 'Paid',
  organic: 'Organic',
  outbound: 'Outbound',
};

export const LEAD_CAMPAIGN_MAX = 200;

export const LEAD_TYPES = ['client_project', 'freelance', 'freelance_project'] as const;
export type LeadType = (typeof LEAD_TYPES)[number];

export const LEAD_TYPE_LABELS: Record<LeadType, string> = {
  client_project: 'Client project',
  freelance: 'Freelance',
  freelance_project: 'Freelance project',
};

export interface LeadRow {
  external_source_ref: string | null;
  strategy_id: string | null;
  attribution: 'auto' | 'manual' | null;
  acquisition_manual: number;
  id: string;
  name: string;
  client_id: string | null;
  project_id: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  company: string | null;
  website: string | null;
  address: string | null;
  secondary_contact_name: string | null;
  secondary_contact_email: string | null;
  status: LeadStatus;
  source: LeadSource | null;
  lead_type: LeadType;
  request_type: LeadRequestType | null;
  acquisition: LeadAcquisition | null;
  campaign: string | null;
  value_cents: number | null;
  enquiry_date: string | null;
  followup_date: string | null;
  closed_date: string | null;
  resurface_date: string | null;
  notes: string | null;
  notion_id: string | null;
  created_at: number;
  updated_at: number;
  won_at: number | null;
  deleted_at: number | null;
}

export interface LeadWithNames extends LeadRow {
  client_name: string | null;
  converted_project_name: string | null;
  converted_project_number: number | null;
  strategy_name: string | null;
  strategy_channel_name: string | null;
}

export interface LeadUpsert {
  strategy_id?: string | null;
  attribution?: 'auto' | 'manual' | null;
  acquisition_manual?: number;
  name: string;
  client_id?: string | null;
  contact_name?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  company?: string | null;
  website?: string | null;
  address?: string | null;
  secondary_contact_name?: string | null;
  secondary_contact_email?: string | null;
  status?: LeadStatus;
  source?: LeadSource | null;
  lead_type?: LeadType;
  request_type?: LeadRequestType | null;
  acquisition?: LeadAcquisition | null;
  campaign?: string | null;
  value_cents?: number | null;
  enquiry_date?: string | null;
  followup_date?: string | null;
  closed_date?: string | null;
  resurface_date?: string | null;
  notes?: string | null;
}

// ---------- Projects ----------

export const BRANDS = ['hsp', 'ss'] as const;
export type Brand = (typeof BRANDS)[number];

export const PROJECT_PHASES = [
  'lead',
  'pre-production',
  'shoot',
  'post',
  'delivered',
  'clean_up',
  'closed',
  'on_hold',
] as const;
export type ProjectPhase = (typeof PROJECT_PHASES)[number];

// "Onboarding" relabel: the DB keeps enum value 'lead' for project phase;
// every UI label says Onboarding (design ledger — leads never appear on the
// Projects page).
export const PHASE_LABELS: Record<ProjectPhase, string> = {
  'lead': 'Onboarding',
  'pre-production': 'Pre-production',
  'shoot': 'Shoot',
  'post': 'Post',
  'delivered': 'Delivered',
  'clean_up': 'Clean up',
  'closed': 'Closed',
  'on_hold': 'On hold',
};

/** Compact variant for card pills — "Pre-prod" per the mockup cards. */
export const PHASE_PILL_LABELS: Record<ProjectPhase, string> = {
  ...PHASE_LABELS,
  'pre-production': 'Pre-prod',
};

/** Position on the lead → delivered pipeline, for progress bars. closed/on_hold sit outside it. */
export const PHASE_PROGRESS: Record<ProjectPhase, number | null> = {
  'lead': 0.1,
  'pre-production': 0.3,
  'shoot': 0.55,
  'post': 0.8,
  'delivered': 1,
  'clean_up': 1,
  'closed': null,
  'on_hold': null,
};

export const QUOTE_STATUSES = [
  'not_started',
  'proposal_sent',
  'approved',
  'sent',
  'deposit_paid',
  'free_project',
  'remainder_paid',
] as const;
export type QuoteStatus = (typeof QUOTE_STATUSES)[number];

export const QUOTE_STATUS_LABELS: Record<QuoteStatus, string> = {
  not_started: 'Not started',
  proposal_sent: 'Proposal sent',
  approved: 'Approved',
  sent: 'Sent',
  deposit_paid: 'Deposit paid',
  free_project: 'Free project',
  remainder_paid: 'Remainder paid',
};

export const SOCIAL_STATUSES = [
  'posted',
  'post_soon',
  'content_approved',
  'waiting_approval',
  'request_permission',
  'not_posting',
  'not_to_post',
  'not_public',
  'na',
] as const;
export type SocialStatus = (typeof SOCIAL_STATUSES)[number];

export const SOCIAL_STATUS_LABELS: Record<SocialStatus, string> = {
  posted: 'Posted',
  post_soon: 'Post soon',
  content_approved: 'Content approved',
  waiting_approval: 'Waiting on approval',
  request_permission: 'Request permission',
  not_posting: 'Not posting',
  not_to_post: 'Not to post',
  not_public: 'Project not public',
  na: 'N/A',
};

export const SOCIAL_ASSET_TYPES = ['social_made', 'bts', 'stills', 'selects', 'frames', 'ptc'] as const;
export type SocialAssetType = (typeof SOCIAL_ASSET_TYPES)[number];

export const SOCIAL_ASSET_STATUSES = ['needs_prep', 'ready', 'scheduled', 'used', 'restricted'] as const;
export type SocialAssetStatus = (typeof SOCIAL_ASSET_STATUSES)[number];

export const SOCIAL_ASSET_CHANNELS = ['ig', 'linkedin'] as const;
export type SocialAssetChannel = (typeof SOCIAL_ASSET_CHANNELS)[number];

export const OUTREACH_LEAD_STATUSES = ['new', 'reached_out', 'followup', 'replied', 'closed'] as const;
export type OutreachLeadStatus = (typeof OUTREACH_LEAD_STATUSES)[number];

export const OUTREACH_CLOSED_REASONS = ['won', 'not_a_fit'] as const;
export type OutreachClosedReason = (typeof OUTREACH_CLOSED_REASONS)[number];

export const OUTREACH_VERDICTS = ['shortlist', 'queued'] as const;
export type OutreachVerdict = (typeof OUTREACH_VERDICTS)[number];

export const OUTREACH_VERTICALS = ['healthcare', 'professional-services'] as const;
export type OutreachVertical = (typeof OUTREACH_VERTICALS)[number];

export const PROJECT_KINDS = ['client_project', 'freelance', 'freelance_project'] as const;
export type ProjectKind = (typeof PROJECT_KINDS)[number];

/** Freelance kinds must tick "Selects exported" before closing from Clean up. */
export const SELECTS_REQUIRED_KINDS: readonly ProjectKind[] = ['freelance', 'freelance_project'];

export const PROJECT_KIND_LABELS: Record<ProjectKind, string> = {
  client_project: 'Client project',
  freelance: 'Freelance',
  freelance_project: 'Freelance project',
};

// Datalist suggestions only — not DB-enforced; drives and formats change.
export const ARCHIVE_DRIVES = [
  'HS 32TB RAID',
  'HS 12TB HD1',
  'HS 4TB HD1',
  'HS 4TB HD2',
  'HS 4TB HD3',
  'HS 1TB SSD',
  'HSP SSD',
  'HSP 512SSD',
  'Henry Passport',
  'No Footage',
  'Previous Project',
] as const;

export const VIDEO_TYPES = [
  'How-To Explainer',
  'Event Capture',
  'Update Video',
  'Interview/B-Roll Package',
  'Motion Graphics',
  'Facebook Ads',
  "Run'n'Gun IVs + B-Roll",
  'Piece to Cameras',
  'Podcast',
  'Photography',
] as const;

export const VIDEO_INDUSTRIES = [
  'Trades',
  'Property',
  'Exhibition',
  'Government',
  'Health',
  'Entrepreneurship',
  'Construction',
  'Agriculture',
  'Sport',
  'Tech',
  'Education',
  'Coaching',
  'Disability',
  'Food',
] as const;

export interface ProjectRow {
  /** Optional during backend-only rollout for older cached DTOs. D1 always stores 0 or 1. */
  no_footage?: number;
  /** ms timestamp — freelance kinds need it set before Clean up → Closed. */
  selects_exported_at?: number | null;
  id: string;
  client_id: string;
  name: string;
  brand: Brand;
  /** Henry's sequential archive index (#0231 style). Auto-assigned at create
      once the backfill has run; null until then. */
  project_number: number | null;
  phase: ProjectPhase;
  /** Per-project contact override; null inherits the client's contact. */
  /** Optional in the shared DTO during the backend-only rollout so existing
      client-side fixtures remain valid until the separately gated UI phase. */
  contact_id?: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  dashboard_slug: string | null;
  summary: string | null;
  value_cents: number | null;
  start_date: string | null;
  due_date: string | null;
  delivered_date: string | null;
  project_kind: ProjectKind;
  quote_status: QuoteStatus | null;
  project_types: string | null;
  video_type: string | null;
  video_industry: string | null;
  shoot_date: string | null;
  /** Inclusive last day of a multi-day shoot; NULL = single-day (0049). */
  shoot_end_date: string | null;
  edit_date: string | null;
  /** Inclusive last day of a multi-day edit period; NULL = single-day. */
  edit_end_date: string | null;
  archive_drive_1: string | null;
  archive_drive_2: string | null;
  masters: number;
  backup: number;
  cloud: number;
  social_status: SocialStatus | null;
  social_assets_prepared: number;
  social_release_date: string | null;
  social_restricted: number;
  social_restricted_note: string | null;
  gd_link: string | null;
  youtube_link: string | null;
  /** Deep links into the embedded tools (Tools rail group, migration 0010). */
  script_url: string | null;
  callsheet_url: string | null;
  proposal_url: string | null;
  /** Testimonial ask on delivery (Phase 2.3, migration 0027). `*_at` are the
      cron send stamps (idempotency); `closed_at`/`closed_reason` are set when
      Henry marks it responded or skips it — either stops the automation. */
  testimonial_requested_at: number | null;
  testimonial_reminder_at: number | null;
  testimonial_closed_at: number | null;
  testimonial_closed_reason: string | null;
  testimonial_video_key?: string | null;
  testimonial_permission?: number | null;
  /** Explicit operator-controlled waiting state (Phase 2.1). The cron sends
      after 4 quiet days, then weekly, capped at three; snooze is a
      Melbourne-local YYYY-MM-DD. */
  waiting_on_client_since: number | null;
  waiting_nudge_count: number;
  waiting_last_nudge_at: number | null;
  waiting_snoozed_until: string | null;
  /** Payment-gated delivery page (Phase 3.2, migration 0033).
      `delivery_token` is the unguessable public-page token, generated on
      demand — see lib/deliveryLink.ts's ensureDeliveryToken. `gating_invoice_id`
      is the invoice (if any) that must be paid before delivery links
      reveal; NULL means the page is ungated. See shared/delivery.ts for the
      gating decision. */
  delivery_token: string | null;
  gating_invoice_id: string | null;
  notion_id: string | null;
  /** Operator-controlled archive state. Unlike deleted_at, this keeps the
      project and every linked invoice live; unpaid invoices are ignored by
      reminders/outstanding totals while later payments still count. */
  voided_at: number | null;
  created_at: number;
  updated_at: number;
  deleted_at: number | null;
}

export interface ProjectWithClient extends ProjectRow {
  /** List endpoint: linked drive labels across every role. */
  drive_labels?: string[];
  /** List endpoint: a live, non-automatic drive is linked with role backup. */
  has_manual_backup?: boolean;
  /** List endpoint: alphabetically first live, non-automatic backup drive label. */
  manual_backup_label?: string | null;
  client_name: string;
}

/** POST /projects/:id/close — Delivered/Clean up → Closed. Exactly one of
    backup_drive_id / no_footage: true. Freelance kinds also need
    selects_exported: true unless selects_exported_at is already set. */
export interface ProjectCloseInput {
  backup_drive_id?: string;
  no_footage?: boolean;
  selects_exported?: boolean;
}

export interface ProjectUpsert {
  client_id: string;
  name: string;
  brand?: Brand;
  project_number?: number | null;
  phase?: ProjectPhase;
  /** Virtual PATCH input validated against a live contact belonging to the
      project's effective client; null clears the picked contact. */
  contact_id?: string | null;
  contact_name?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  dashboard_slug?: string | null;
  summary?: string | null;
  value_cents?: number | null;
  start_date?: string | null;
  due_date?: string | null;
  delivered_date?: string | null;
  /** Virtual PATCH transition input; routes/projects.ts atomically owns the
      four persisted waiting/automation fields. */
  waiting_on_client?: boolean;
  waiting_snoozed_until?: string | null;
  /** Virtual PATCH input (Phase 3.2) — routes/projects.ts validates this is
      one of the project's own, non-deleted invoices (or null) before
      persisting; not part of the synchronous parseUpsert() field set. */
  gating_invoice_id?: string | null;
  project_kind?: ProjectKind;
  quote_status?: QuoteStatus | null;
  project_types?: string | null;
  video_type?: string | null;
  video_industry?: string | null;
  shoot_date?: string | null;
  shoot_end_date?: string | null;
  edit_date?: string | null;
  edit_end_date?: string | null;
  archive_drive_1?: string | null;
  archive_drive_2?: string | null;
  masters?: number;
  backup?: number;
  cloud?: number;
  social_status?: SocialStatus | null;
  social_assets_prepared?: number;
  social_release_date?: string | null;
  social_restricted?: number;
  social_restricted_note?: string | null;
  gd_link?: string | null;
  youtube_link?: string | null;
  script_url?: string | null;
  callsheet_url?: string | null;
  proposal_url?: string | null;
}

// ---------- Social assets ----------

export interface SocialAssetRow {
  id: string;
  project_id: string | null;
  type: SocialAssetType;
  label: string;
  url: string | null;
  note: string | null;
  brand: Brand;
  status: SocialAssetStatus;
  prior_status: SocialAssetStatus | null;
  restricted_note: string | null;
  planned_date: string | null;
  channels: string | null;
  used_at: number | null;
  created_at: number;
  updated_at: number;
  deleted_at: number | null;
}

export interface SocialAssetWithProject extends SocialAssetRow {
  project_name: string | null;
  project_social_restricted: number;
  project_social_restricted_note: string | null;
  effective_restricted: boolean;
}

// ---------- Outreach leads ----------

export interface OutreachLeadRow {
  id: string;
  place_id: string;
  domain: string;
  name: string;
  suburb: string;
  vertical: OutreachVertical;
  verdict: OutreachVerdict;
  score: number;
  band: string;
  score_breakdown: string;
  run_date: string;
  why_they_fit: string;
  outreach_angle: string;
  video_opportunities: string;
  decision_maker: string;
  links: string;
  contact: string;
  status: OutreachLeadStatus;
  closed_reason: OutreachClosedReason | null;
  promoted_lead_id: string | null;
  promoted_at: number | null;
  replied_at: number | null;
  created_at: number;
  updated_at: number;
  deleted_at: number | null;
}

// ---------- Project artifacts (Wave A1 — the artifacts spine) ----------
// Link rows between a project and its draft in a standalone tool (script/
// callsheet/proposal/review). See docs/HANDOFF-deploy-domains-integration.md Part C1.

export const ARTIFACT_TOOLS = ['script', 'callsheet', 'proposal', 'review'] as const;
export type ArtifactTool = (typeof ARTIFACT_TOOLS)[number];

export const ARTIFACT_TOOL_LABELS: Record<ArtifactTool, string> = {
  script: 'Script',
  callsheet: 'Call sheet',
  proposal: 'Proposal',
  review: 'Review',
};

export interface ProjectArtifactRow {
  id: string;
  project_id: string;
  tool: ArtifactTool;
  /** Script kind or call sheet document type; null for legacy/other artifacts. */
  artifact_type: string | null;
  external_id: string | null;
  slug: string | null;
  title: string | null;
  admin_url: string | null;
  public_url: string | null;
  /** Review share credentials are minted once; this row ID enables rotation. */
  review_share_link_id?: string | null;
  /** Short-lived claim serializes review-link operations across requests. */
  review_link_claimed_at?: number | null;
  /** Monotonic producer fencing counter; older requests cannot rotate newer links. */
  review_link_generation?: number;
  /** Round where this artifact was first published to the client dashboard. */
  review_manifest_round?: number | null;
  /** Cached verbatim from the manifest entry — opaque, tool-owned. */
  status: string | null;
  status_synced_at: number | null;
  /** Deposit-at-signature (Wave C3, migration 0020). Set once a signed
      event auto-issues this artifact's deposit invoice — idempotency
      marker so a duplicate signed event reuses it instead of creating a
      second invoice. Proposals only; null for scripts/callsheets. */
  deposit_invoice_id: string | null;
  /** JSON-stringified registration package prices (fix batch 2026-07,
      migration 0025), used to validate signed-event deposit_cents. */
  packages_json: string | null;
  created_at: number;
  updated_at: number;
}

/** A manifest entry with no matching `project_artifacts` row — rendered as
    "found on dashboard — link to this project?" with one-click adopt. */
export interface UnlinkedArtifactCandidate {
  tool: ArtifactTool;
  external_id: string | null;
  title: string | null;
  url: string | null;
  status: string | null;
}

// ---------- Project deliverables (tracking + payment-gated delivery) ----------
// Planned work may have no URL. Public delivery still lists URL-bearing rows
// once the project's payment gate is open; tracking status does not gate it.

export interface ProjectDeliverableRow {
  id: string;
  project_id: string;
  label: string;
  url: string | null;
  note: string | null;
  type: DeliverableType;
  status: DeliverableStatus;
  due_date: string | null;
  delivered_at: string | null;
  review_artifact_id: string | null;
  media_asset_ref: string | null;
  sort_order: number;
  created_at: number;
  updated_at: number;
  deleted_at: number | null;
}

export type ProjectDeliverableUpsert = Pick<ProjectDeliverableRow, 'label'> &
  Partial<Pick<ProjectDeliverableRow, 'url' | 'note' | 'type' | 'status' | 'due_date' |
  'delivered_at' | 'review_artifact_id' | 'media_asset_ref' | 'sort_order'>>;

// ---------- Unlinked artifacts queue (Wave A3) ----------
// A proposal (or other tool) self-registered via POST /api/service/artifacts
// with a client_name that didn't confidently match a clients row. Settings →
// System surfaces these for manual "assign to project". See migration 0019.

export const UNLINKED_ARTIFACT_STATUSES = ['pending', 'assigned'] as const;
export type UnlinkedArtifactStatus = (typeof UNLINKED_ARTIFACT_STATUSES)[number];

export interface UnlinkedArtifactRow {
  id: string;
  tool: ArtifactTool;
  external_id: string | null;
  title: string | null;
  public_url: string | null;
  client_name: string;
  dashboard_slug: string | null;
  status: UnlinkedArtifactStatus;
  assigned_project_id: string | null;
  /** JSON-stringified registration package prices (fix batch 2026-07,
      migration 0025), carried through when manually assigned. */
  packages_json: string | null;
  received_at: number;
  updated_at: number;
}

// ---------- Crew contacts (Phase D2 — crew & vendor register) ----------
// Freelance crew/vendor directory with independently entered full- and
// half-day rates, seeded from the vault's contractors.md roster (C5's
// seed-data plan). `role` is free text — see migrations/0023_crew_contacts.sql
// and 0040_crew_half_days.sql. The call sheet tool reads a subset via GET
// /api/service/crew (both rates excluded — see routes/service.ts).

export interface CrewContactRow {
  id: string;
  name: string;
  role: string | null;
  email: string | null;
  phone: string | null;
  company: string | null;
  day_rate_cents: number | null;
  half_day_rate_cents: number | null;
  notes: string | null;
  notion_id: string | null;
  created_at: number;
  updated_at: number;
  deleted_at: number | null;
}

export interface CrewContactUpsert {
  name: string;
  role?: string | null;
  email?: string | null;
  phone?: string | null;
  company?: string | null;
  day_rate_cents?: number | null;
  half_day_rate_cents?: number | null;
  notes?: string | null;
}

/** GET /api/admin/crew's list shape (Phase 3.3) — current_bookings is a
    quiet per-row hint: non-deleted project_crew rows for this person whose
    end_date hasn't passed yet (Melbourne today). */
export interface CrewContactWithBookings extends CrewContactRow {
  current_bookings: number;
}

// ---------- Project crew bookings (Phase 3.3 — "On this job", mockup #4) --
// Nested subresource of a project: which crew_contacts person is booked, for
// what inclusive date range, and which individual dates are half days.
// day_rate_cents and half_day_rate_cents snapshot independently entered
// register rates at booking time but stay editable per booking. The raw
// half_day_dates JSON column is null for all-full bookings. See migrations/
// 0031_project_crew.sql and 0040_crew_half_days.sql.

export interface ProjectCrewRow {
  id: string;
  project_id: string;
  crew_id: string;
  role: string | null;
  day_rate_cents: number;
  half_day_dates: string | null;
  half_day_rate_cents: number | null;
  start_date: string;
  end_date: string;
  created_at: number;
  updated_at: number;
  deleted_at: number | null;
}

export interface ProjectCrewWithName extends ProjectCrewRow {
  crew_name: string;
}

/** Another (non-deleted) booking for the same crew member on a DIFFERENT
    project whose inclusive date range overlaps this one — surfaced by the
    create/patch response as a warning pill; it never blocks the save. */
export interface ProjectCrewConflict {
  project_id: string;
  project_name: string;
  start_date: string;
  end_date: string;
}

export interface ProjectCrewUpsert {
  crew_id: string;
  role?: string | null;
  day_rate_cents?: number | null;
  half_day_dates?: string | null;
  half_day_rate_cents?: number | null;
  start_date: string;
  end_date: string;
}

// ---------- Tasks ----------

export const TASK_STATUSES = ['todo', 'in_progress', 'done'] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];

export interface TaskRecurrenceRow {
  id: string;
  title: string;
  notes: string | null;
  priority: number;
  cadence: 'weekly' | 'monthly';
  anchor: string;
  next_due: string;
  created_at: number;
  updated_at: number;
  deleted_at: number | null;
  paused_at: number | null;
}

export interface TaskRecurrenceUpsert {
  title: string;
  notes?: string | null;
  priority?: number;
  cadence: 'weekly' | 'monthly';
  anchor: string;
}

export interface TaskRow {
  id: string;
  project_id: string | null;
  client_id: string | null;
  recurrence_id: string | null;
  title: string;
  notes: string | null;
  status: TaskStatus;
  priority: number;
  due_date: string | null;
  completed_at: number | null;
  sort_order: number;
  notion_id: string | null;
  created_at: number;
  updated_at: number;
  deleted_at: number | null;
}

// ---------- Dashboards (hub data passthrough) ----------

export interface DashboardSummaryLite {
  slug: string;
  client_name: string;
  project_name: string;
  current_phase_label: string;
}

// ---------- Finance ----------

export const INVOICE_STATUSES = ['draft', 'sent', 'paid', 'overdue', 'void'] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

export const INVOICE_KINDS = ['deposit', 'remainder', 'full', 'other'] as const;
export type InvoiceKind = (typeof INVOICE_KINDS)[number];

export interface InvoiceLineInput {
  description: string;
  quantity_milli: number;
  unit_ex_cents: number;
}

export interface InvoiceLineRow extends InvoiceLineInput {
  id: string;
  invoice_id: string;
  position: number;
  line_ex_cents: number;
  created_at: number;
  updated_at: number;
}

export interface InvoiceRow {
  /** Migration 0065: immutable identity and payment details at first issue. */
  business_snapshot_json?: string | null;
  id: string;
  client_id: string;
  project_id: string | null;
  brand: Brand;
  invoice_number: string;
  amount_cents: number;
  gst_cents: number;
  status: InvoiceStatus;
  invoice_kind: InvoiceKind | null;
  issue_date: string | null;
  due_date: string | null;
  paid_date: string | null;
  external_url: string | null;
  notes: string | null;
  /** Per-invoice contact override; null inherits the client's contact. */
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  /** Stripe direct + webhook relay (Wave C1, migration 0018). Set when a
      Payment Link has been generated / a payment has landed for this
      invoice; both null until then. */
  stripe_payment_link_id: string | null;
  stripe_payment_link_version: string | null;
  stripe_payment_intent_id: string | null;
  /** Client-facing invoicing (Wave C2, migration 0020). `description` is a
      one-line, client-safe summary shown on the public invoice page —
      deliberately separate from `notes` (internal-only, never rendered
      publicly). `public_token` is generated on demand (first share), never
      backfilled. `sent_at`/`last_reminder_at` track the email send +
      reminder-cron history. `stripe_payment_link_url` pairs with C1's
      `stripe_payment_link_id` so the public page can render "Pay now"
      without an extra Stripe round-trip. */
  description: string | null;
  public_token: string | null;
  sent_at: number | null;
  last_reminder_at: number | null;
  /** Reminder-ladder rung last emailed (Phase 2.2, migration 0026): the day
      offset relative to the due date of the highest stage sent (-3, 0, 7, 14,
      21). NULL = no ladder email sent yet. Makes cron re-runs idempotent. */
  last_reminder_stage: number | null;
  stripe_payment_link_url: string | null;
  notion_id: string | null;
  created_at: number;
  updated_at: number;
  deleted_at: number | null;
}

export interface InvoiceWithNames extends InvoiceRow {
  line_count?: number;
  client_name: string;
  project_name: string | null;
  project_brand: Brand | null;
  /** Set when the linked project is voided — unpaid rows of a voided project
      drop out of the normal status groups so the list agrees with the vitals
      (which already exclude them). NULL for live and standalone invoices. */
  project_voided_at: number | null;
}

export const EXPENSE_CATEGORIES = ['general', 'gear', 'software', 'travel', 'crew', 'subscriptions'] as const;

export interface ExpenseRow {
  id: string;
  project_id: string | null;
  category: string;
  vendor: string | null;
  description: string | null;
  amount_cents: number;
  gst_cents: number;
  expense_date: string;
  receipt_url: string | null;
  notion_id: string | null;
  created_at: number;
  updated_at: number;
  deleted_at: number | null;
}

export interface ExpenseWithNames extends ExpenseRow {
  project_name: string | null;
}

/** One Australian-FY quarter bucket in the reports response. */
export interface QuarterReport {
  key: string;          // e.g. "FY26 Q4"
  start: string;        // ISO first day
  invoiced_cents: number;
  paid_cents: number;
  gst_collected_cents: number;
  expenses_cents: number;
  gst_paid_cents: number;
  net_cents: number;    // paid - expenses
  // Ex-GST variants for the money display toggle (the BAS table itself
  // always stays inc-GST — Henry's call, 2026-06-13).
  invoiced_ex_cents: number;
  paid_ex_cents: number;
  expenses_ex_cents: number;
}

export interface FinanceReport {
  quarters: QuarterReport[];
  outstanding_cents: number;
  outstanding_count: number;
  overdue_cents: number;
  overdue_count: number;
  outstanding_ex_cents: number;
  overdue_ex_cents: number;
}

// Read-only library across the script and call sheet tools.
export type DocumentTool = 'script' | 'callsheet';
export type DocumentStatus = 'draft' | 'in_review' | 'approved' | 'published' | 'archived';

export interface Document {
  key: string;
  tool: DocumentTool;
  external_id: string;
  title: string;
  status: DocumentStatus;
  raw_status: string;
  version: number;
  doc_type: 'script' | 'callsheet' | 'client_schedule';
  shoot_date: string | null;
  is_draft: boolean;
  is_archived: boolean;
  is_cancelled: boolean;
  source_client_name: string | null;
  project_id: string | null;
  project_number: number | null;
  project_name: string | null;
  client_name: string | null;
  admin_url: string | null;
  updated_at: number;
  updated_date: string;
}

export interface DocumentSourceState {
  ok: boolean;
  error: string | null;
  skipped?: boolean;
}

export interface DocumentsResponse {
  documents: Document[];
  projects: {
    id: string;
    project_number: number | null;
    name: string;
    client_name: string | null;
  }[];
  sources: { scripts: DocumentSourceState; callsheets: DocumentSourceState };
  partial: boolean;
  fetched_at: number;
}

// ── Document sends (workflow improvements D1, Oct 2026) ──────────────────
// One row per proposal or quote actually sent to a client. Fed by the
// proposal tool at registration (kind='proposal'), the quote-creator skill
// (kind='quote', D2) and the Stats / detail-page "Log sent" form. The
// "Proposals & quotes sent" goal metric (key `proposals_delivered`) counts
// these by `sent_date`, never `project_artifacts` registrations.

export const DOCUMENT_SEND_KINDS = ['proposal', 'quote'] as const;
export type DocumentSendKind = (typeof DOCUMENT_SEND_KINDS)[number];
export const DOCUMENT_SEND_KIND_LABELS: Record<DocumentSendKind, string> = {
  proposal: 'Proposal',
  quote: 'Quote',
};

export const DOCUMENT_SEND_OUTCOMES = ['pending', 'won', 'lost', 'on_hold'] as const;
export type DocumentSendOutcome = (typeof DOCUMENT_SEND_OUTCOMES)[number];
export const DOCUMENT_SEND_OUTCOME_LABELS: Record<DocumentSendOutcome, string> = {
  pending: 'Awaiting reply',
  won: 'Won',
  lost: 'Lost',
  on_hold: 'On hold',
};

export const DOCUMENT_SEND_SOURCES = ['proposal_tool', 'quote_creator', 'manual', 'backfill'] as const;
export type DocumentSendSource = (typeof DOCUMENT_SEND_SOURCES)[number];

export interface DocumentSendRow {
  id: string;
  kind: DocumentSendKind;
  title: string;
  /** Quote reference such as SS-2026-0151; unique per kind among live rows. */
  ref: string | null;
  client_id: string | null;
  /** Client name as supplied when no client row matched (unlinked). */
  client_name: string | null;
  lead_id: string | null;
  project_id: string | null;
  /** The project_artifacts row this send mirrors (proposal tool feed). */
  artifact_id: string | null;
  /** Melbourne-local YYYY-MM-DD the document was sent. */
  sent_date: string;
  /** 1 when sent_date was estimated by the backfill and not yet confirmed;
      any manual sent_date edit clears it. */
  date_estimated: number;
  /** Ex GST. amount_max_cents only when quoted as a range. */
  amount_min_cents: number | null;
  amount_max_cents: number | null;
  outcome: DocumentSendOutcome;
  outcome_date: string | null;
  /** Ex GST, set when a quote is won at a figure other than the low end. */
  won_amount_cents: number | null;
  /** Explicit ex-GST deposit terms; manual wins only draft on confirmation. */
  deposit_cents?: number | null;
  deposit_invoice_id?: string | null;
  source: DocumentSendSource;
  /** Idempotency key for automatic feeds (proposal external_id / public_url
      for proposal_tool; the quote ref for quote_creator). */
  external_key: string | null;
  notes: string | null;
  admin_url: string | null;
  created_at: number;
  updated_at: number;
  deleted_at: number | null;
}

/** List shape returned by GET /api/admin/document-sends. */
export interface DocumentSendListItem extends DocumentSendRow {
  /** clients.name when linked, otherwise the stored client_name. */
  client_label: string | null;
  lead_name: string | null;
  lead_status: LeadStatus | null;
  project_name: string | null;
  project_number: number | null;
  project_value_cents?: number | null;
  /** From the mirrored artifact: tool admin URL (opens in the OS tool
      embed) and the public URL (new tab), for the "Open ↗" link. */
  artifact_admin_url: string | null;
  artifact_public_url: string | null;
}

/** Inclusive Melbourne dates used for the windowed FY comparison totals. */
export interface StatsComparisonWindow {
  primary: { from: string; to: string };
  comparison: { from: string; to: string };
  year_to_date: boolean;
}

/** FY comparison deltas: pct divides by the absolute baseline, null at zero. */
export type StatsComparisonDeltas = Record<
  'revenue_cents' | 'revenue_ex_cents' | 'expenses_cents' | 'expenses_ex_cents' | 'leads_total' | 'projects_total',
  { abs: number; pct: number | null }
>;

/** Read-only data-quality rows. `amount` is the stored gross amount in cents. */
export interface InvoiceDataQualityRow {
  id: string;
  number: string;
  client: string | null;
  amount: number;
  issue_date: string | null;
  paid_date: string | null;
  status: InvoiceStatus;
}

export type InvoiceDataQualityReport = Record<
  'missing_dates' | 'missing_issue_date' | 'zero_amount' | 'paid_before_issue' | 'duplicate_numbers' | 'missing_client',
  { count: number; invoices: InvoiceDataQualityRow[] }
>;
